import { create } from "zustand";
import {
	getLocalStorage,
	setLocalStorage,
} from "../../../lib/utils/local-storage";
import { AgendaGroup } from "./agenda.types";

export type GroupMode = "flat" | "client";
export type SortField = "outgoing" | "incoming" | "code" | "client";
export type SortDirection = "asc" | "desc";

interface AgendaPersistedState {
	expandedGroupIds: string[];
	filteredGroupIds: string[];
	dateRange: { start: string; end: string };
	viewMode: "day" | "week" | "hour";
	sidebarWidth?: number;
	hourViewDate?: string;
	hourViewShowAllJobs?: boolean;
	dayRange: number;
	hourRange: number;
	weekRange: number;
	groupMode: GroupMode;
	sortField: SortField;
	sortDirection: SortDirection;
	statusFilter: number[];
	leavingSoonEnabled: boolean;
	leavingSoonDays: number;
	rowHeight: number;
}

export interface AgendaState {
	groups: AgendaGroup[];
	agendaLoading: boolean;
	lastPolledAt: Date | null;
	expandedGroupIds: string[];
	filteredGroupIds: string[];
	dateRange: { start: string; end: string };
	viewMode: "day" | "week" | "hour";
	hourViewDate: string;
	hourViewShowAllJobs: boolean;
	sidebarWidth: number;
	profileId: string | null;
	_appId: string | null;
	_fetchFn: (() => Promise<AgendaGroup[]>) | null;
	dayRange: number;
	hourRange: number;
	weekRange: number;
	groupMode: GroupMode;
	sortField: SortField;
	sortDirection: SortDirection;
	statusFilter: number[];
	leavingSoonEnabled: boolean;
	leavingSoonDays: number;
	rowHeight: number;

	setSidebarWidth: (width: number) => void;
	setHourViewShowAllJobs: (value: boolean) => void;
	initAgenda: (
		profileId: string,
		fetchFn: () => Promise<AgendaGroup[]>,
		appId?: string
	) => void;
	fetchAgendaJobs: () => Promise<void>;
	toggleGroupExpanded: (groupId: string) => void;
	expandAllGroups: (groupIds: string[]) => void;
	collapseAllGroups: () => void;
	setFilteredGroupIds: (ids: string[]) => void;
	setDateRange: (start: string, end: string) => void;
	shiftDateRange: (days: number) => void;
	shiftHourViewDate: (days: number) => void;
	setViewMode: (mode: "day" | "week" | "hour") => void;
	resetToToday: () => void;
	setDayRange: (range: number) => void;
	setHourRange: (range: number) => void;
	setWeekRange: (range: number) => void;
	setGroupMode: (mode: GroupMode) => void;
	setSortField: (field: SortField) => void;
	toggleSortDirection: () => void;
	setStatusFilter: (statuses: number[]) => void;
	setLeavingSoonEnabled: (enabled: boolean) => void;
	setLeavingSoonDays: (days: number) => void;
	setRowHeight: (height: number) => void;
}

const getTodayStr = () => new Date().toISOString().split("T")[0];

const getDateRangeForDayRange = (range: number) => {
	const start = new Date();
	start.setDate(start.getDate() - range);
	const end = new Date();
	end.setDate(end.getDate() + range);
	return {
		start: start.toISOString().split("T")[0],
		end: end.toISOString().split("T")[0],
	};
};

const getDateRangeForWeekRange = (range: number) => {
	const start = new Date();
	start.setDate(start.getDate() - range * 7);
	const end = new Date();
	end.setDate(end.getDate() + range * 7);
	return {
		start: start.toISOString().split("T")[0],
		end: end.toISOString().split("T")[0],
	};
};

const getStorageKey = (profileId: string, appId?: string | null) =>
	appId ? `agenda-state-${appId}-${profileId}` : `agenda-state-${profileId}`;

function persistState(get: () => AgendaState) {
	const state = get();
	if (!state.profileId) return;
	const persisted: AgendaPersistedState = {
		expandedGroupIds: state.expandedGroupIds,
		filteredGroupIds: state.filteredGroupIds,
		dateRange: state.dateRange,
		viewMode: state.viewMode,
		sidebarWidth: state.sidebarWidth,
		hourViewDate: state.hourViewDate,
		hourViewShowAllJobs: state.hourViewShowAllJobs,
		dayRange: state.dayRange,
		hourRange: state.hourRange,
		weekRange: state.weekRange,
		groupMode: state.groupMode,
		sortField: state.sortField,
		sortDirection: state.sortDirection,
		statusFilter: state.statusFilter,
		leavingSoonEnabled: state.leavingSoonEnabled,
		leavingSoonDays: state.leavingSoonDays,
		rowHeight: state.rowHeight,
	};
	setLocalStorage(getStorageKey(state.profileId, state._appId), persisted);
}

export const useAgendaStore = create<AgendaState>()((set, get) => ({
	groups: [],
	agendaLoading: false,
	lastPolledAt: null,
	expandedGroupIds: [],
	filteredGroupIds: [],
	dateRange: getDateRangeForDayRange(1),
	viewMode: "day",
	hourViewDate: getTodayStr(),
	hourViewShowAllJobs: false,
	sidebarWidth: 200,
	profileId: null,
	_appId: null,
	_fetchFn: null,
	dayRange: 1,
	hourRange: 7,
	weekRange: 2,
	groupMode: "flat",
	sortField: "outgoing",
	sortDirection: "asc",
	statusFilter: [],
	leavingSoonEnabled: false,
	leavingSoonDays: 1,
	rowHeight: 24,

	setSidebarWidth: (width: number) => {
		set({ sidebarWidth: width });
		persistState(get);
	},

	setHourViewShowAllJobs: (value: boolean) => {
		set({ hourViewShowAllJobs: value });
		persistState(get);
	},

	initAgenda: (profileId, fetchFn, appId) => {
		const persisted = getLocalStorage<AgendaPersistedState | null>(
			getStorageKey(profileId, appId),
			null
		);
		set({
			profileId,
			_appId: appId ?? null,
			_fetchFn: fetchFn,
			...(persisted
				? {
						expandedGroupIds: persisted.expandedGroupIds,
						filteredGroupIds: persisted.filteredGroupIds,
						dateRange: persisted.dateRange,
						viewMode: persisted.viewMode,
						sidebarWidth: persisted.sidebarWidth ?? 200,
						hourViewDate: persisted.hourViewDate ?? getTodayStr(),
						hourViewShowAllJobs: persisted.hourViewShowAllJobs ?? false,
						dayRange: persisted.dayRange ?? 1,
						hourRange: persisted.hourRange ?? 7,
						weekRange: persisted.weekRange ?? 2,
						groupMode: persisted.groupMode ?? "flat",
						sortField: persisted.sortField ?? "outgoing",
						sortDirection: persisted.sortDirection ?? "asc",
						statusFilter: persisted.statusFilter ?? [],
						leavingSoonEnabled: persisted.leavingSoonEnabled ?? false,
						leavingSoonDays: persisted.leavingSoonDays ?? 1,
						rowHeight: persisted.rowHeight ?? 24,
					}
				: {}),
		});
	},

	fetchAgendaJobs: async () => {
		const fetchFn = get()._fetchFn;
		if (!fetchFn) return;
		set({ agendaLoading: true });
		try {
			const groups = await fetchFn();
			set({ groups, agendaLoading: false, lastPolledAt: new Date() });
		} catch {
			set({ agendaLoading: false });
		}
	},

	toggleGroupExpanded: (groupId: string) => {
		const ids = get().expandedGroupIds;
		const idx = ids.indexOf(groupId);
		set({
			expandedGroupIds:
				idx >= 0
					? ids.filter((_, i) => i !== idx)
					: [...ids, groupId],
		});
		persistState(get);
	},

	expandAllGroups: (groupIds: string[]) => {
		set({ expandedGroupIds: groupIds });
		persistState(get);
	},

	collapseAllGroups: () => {
		set({ expandedGroupIds: [] });
		persistState(get);
	},

	setFilteredGroupIds: (ids: string[]) => {
		set({ filteredGroupIds: ids });
		persistState(get);
	},

	setDateRange: (start: string, end: string) => {
		set({ dateRange: { start, end } });
		persistState(get);
	},

	shiftDateRange: (days: number) => {
		const { dateRange } = get();
		const start = new Date(dateRange.start);
		const end = new Date(dateRange.end);
		start.setDate(start.getDate() + days);
		end.setDate(end.getDate() + days);
		set({
			dateRange: {
				start: start.toISOString().split("T")[0],
				end: end.toISOString().split("T")[0],
			},
		});
		persistState(get);
	},

	shiftHourViewDate: (days: number) => {
		const d = new Date(get().hourViewDate + "T12:00:00");
		d.setDate(d.getDate() + days);
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, "0");
		const day = String(d.getDate()).padStart(2, "0");
		set({ hourViewDate: `${y}-${m}-${day}` });
		persistState(get);
	},

	setViewMode: (mode: "day" | "week" | "hour") => {
		const state = get();
		let dateRange = state.dateRange;
		if (mode === "day") {
			dateRange = getDateRangeForDayRange(state.dayRange);
		} else if (mode === "week") {
			dateRange = getDateRangeForWeekRange(state.weekRange);
		}
		set({ viewMode: mode, dateRange });
		persistState(get);
	},

	resetToToday: () => {
		const state = get();
		let dateRange;
		if (state.viewMode === "week") {
			dateRange = getDateRangeForWeekRange(state.weekRange);
		} else {
			dateRange = getDateRangeForDayRange(state.dayRange);
		}
		set({ dateRange, hourViewDate: getTodayStr() });
		persistState(get);
	},

	setDayRange: (range: number) => {
		const dateRange = getDateRangeForDayRange(range);
		set({ dayRange: range, dateRange });
		persistState(get);
	},

	setHourRange: (range: number) => {
		set({ hourRange: range });
		persistState(get);
	},

	setWeekRange: (range: number) => {
		const dateRange = getDateRangeForWeekRange(range);
		set({ weekRange: range, dateRange });
		persistState(get);
	},

	setGroupMode: (mode: GroupMode) => {
		set({ groupMode: mode });
		persistState(get);
	},

	setSortField: (field: SortField) => {
		set({ sortField: field });
		persistState(get);
	},

	toggleSortDirection: () => {
		set({ sortDirection: get().sortDirection === "asc" ? "desc" : "asc" });
		persistState(get);
	},

	setStatusFilter: (statuses: number[]) => {
		set({ statusFilter: statuses });
		persistState(get);
	},

	setLeavingSoonEnabled: (enabled: boolean) => {
		set({ leavingSoonEnabled: enabled });
		persistState(get);
	},

	setLeavingSoonDays: (days: number) => {
		set({ leavingSoonDays: days });
		persistState(get);
	},

	setRowHeight: (height: number) => {
		set({ rowHeight: height });
		persistState(get);
	},
}));
